import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Upload, FileSpreadsheet, ArrowRight, Check, AlertTriangle, Loader2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";
import {
  autoMapImportColumns,
  parseImportCsv,
} from "@shared/csv-import-mapping";

interface CSVRow {
  [key: string]: string;
}

interface FieldMapping {
  csvColumn: string;
  targetField: string;
}

const CLIENT_FIELDS = [
  { value: "", label: "-- Do not import --" },
  { value: "accountName", label: "Account Name" },
  { value: "contactName", label: "Contact Name" },
  { value: "email", label: "Email" },
  { value: "phoneNumber", label: "Phone Number" },
  { value: "address", label: "Address" },
  { value: "city", label: "City" },
  { value: "state", label: "State" },
  { value: "zip", label: "Zip" },
  { value: "region", label: "Region" },
  { value: "rep", label: "Rep" },
  { value: "eventRate", label: "Event Rate" },
  { value: "createdBy", label: "Created By (User ID)" },
  { value: "talentName", label: "Talent (Name)" },
];

const INVOICE_FIELDS = [
  { value: "", label: "-- Do not import --" },
  { value: "invoiceNumber", label: "Invoice Number" },
  { value: "dateOfEvent", label: "Date of Event" },
  { value: "bookingDetails", label: "Booking Details" },
  { value: "billingNotes", label: "Billing Notes" },
];

const PAYMENT_FIELDS = [
  { value: "", label: "-- Do not import --" },
  { value: "isPaid", label: "Paid (TRUE/FALSE)" },
  { value: "datePaid", label: "Date Paid" },
  { value: "paymentType", label: "Payment Type" },
  { value: "paymentNotes", label: "Payment Notes" },
];

const BOOKING_FIELDS = [
  { value: "", label: "-- Do not import --" },
  { value: "dateBooked", label: "Date Booked" },
];

interface ImportPageProps {
  effectiveRole?: string;
}

export default function ImportPage({ effectiveRole }: ImportPageProps) {
  const [csvData, setCsvData] = useState<CSVRow[]>([]);
  const [csvColumns, setCsvColumns] = useState<string[]>([]);
  const [mappings, setMappings] = useState<Record<string, string>>({});
  const [step, setStep] = useState<"upload" | "mapping" | "preview" | "importing" | "complete">("upload");
  const [importResults, setImportResults] = useState<{ 
    imported: number; 
    skipped: number; 
    errors: Array<string | { row: number; accountName: string; error: string }>;
    errorSummary?: string[];
  }>({ imported: 0, skipped: 0, errors: [] });
  const { toast } = useToast();

  const parseCSV = (text: string): { columns: string[]; rows: CSVRow[] } =>
    parseImportCsv(text);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    toast({
      title: "Loading File",
      description: `Reading ${file.name}...`,
    });

    const reader = new FileReader();
    
    reader.onerror = () => {
      toast({
        title: "File Read Error",
        description: "Could not read the file. Please try again.",
        variant: "destructive",
      });
    };
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        if (!text) {
          toast({
            title: "Empty File",
            description: "The file appears to be empty.",
            variant: "destructive",
          });
          return;
        }
        
        const { columns, rows } = parseCSV(text);
        
        if (columns.length === 0) {
          toast({
            title: "Invalid CSV",
            description: "Could not parse CSV file. Please check the format.",
            variant: "destructive",
          });
          return;
        }

        setCsvColumns(columns);
        setCsvData(rows);

        setMappings(autoMapImportColumns(columns));
        setStep("mapping");
        
        toast({
          title: "CSV Loaded",
          description: `Found ${rows.length} records with ${columns.length} columns.`,
        });
      } catch (err: any) {
        toast({
          title: "Parse Error",
          description: err.message || "Failed to parse CSV file.",
          variant: "destructive",
        });
      }
    };
    reader.readAsText(file);
  };

  const updateMapping = (csvColumn: string, targetField: string) => {
    setMappings(prev => ({
      ...prev,
      [csvColumn]: targetField,
    }));
  };

  const allFields = [...CLIENT_FIELDS, ...INVOICE_FIELDS, ...PAYMENT_FIELDS, ...BOOKING_FIELDS];
  
  const getFieldLabel = (value: string) => {
    const field = allFields.find(f => f.value === value);
    return field?.label || value;
  };

  const getFieldCategory = (value: string): string => {
    if (CLIENT_FIELDS.find(f => f.value === value)) return "Client";
    if (INVOICE_FIELDS.find(f => f.value === value)) return "Invoice";
    if (PAYMENT_FIELDS.find(f => f.value === value)) return "Payment";
    if (BOOKING_FIELDS.find(f => f.value === value)) return "Booking";
    return "";
  };

  const previewData = useMemo(() => {
    return csvData.slice(0, 5).map(row => {
      const mapped: Record<string, string> = {};
      Object.entries(mappings).forEach(([csvCol, targetField]) => {
        if (targetField) {
          mapped[targetField] = row[csvCol] || "";
        }
      });
      return mapped;
    });
  }, [csvData, mappings]);

  const importMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/import/bulk", {
        data: csvData,
        mappings,
      });
      return response.json();
    },
    onSuccess: (data) => {
      setImportResults(data);
      setStep("complete");
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
      toast({
        title: "Import Complete",
        description: `Imported ${data.imported} records.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive",
      });
      setStep("preview");
    },
  });

  const startImport = () => {
    setStep("importing");
    importMutation.mutate();
  };

  const resetImport = () => {
    setCsvData([]);
    setCsvColumns([]);
    setMappings({});
    setStep("upload");
    setImportResults({ imported: 0, skipped: 0, errors: [] });
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Bulk Import</h1>
        <p className="text-muted-foreground">Import clients, invoices, and payment records from a CSV file</p>
      </div>

      <div className="flex gap-2 mb-6">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === "upload" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
          <span className="w-6 h-6 rounded-full bg-background text-foreground flex items-center justify-center text-sm font-medium">1</span>
          <span>Upload</span>
        </div>
        <ArrowRight className="w-4 h-4 self-center text-muted-foreground" />
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === "mapping" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
          <span className="w-6 h-6 rounded-full bg-background text-foreground flex items-center justify-center text-sm font-medium">2</span>
          <span>Map Fields</span>
        </div>
        <ArrowRight className="w-4 h-4 self-center text-muted-foreground" />
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === "preview" || step === "importing" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
          <span className="w-6 h-6 rounded-full bg-background text-foreground flex items-center justify-center text-sm font-medium">3</span>
          <span>Preview & Import</span>
        </div>
        <ArrowRight className="w-4 h-4 self-center text-muted-foreground" />
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${step === "complete" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
          <span className="w-6 h-6 rounded-full bg-background text-foreground flex items-center justify-center text-sm font-medium">4</span>
          <span>Complete</span>
        </div>
      </div>

      {step === "upload" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload CSV File
            </CardTitle>
            <CardDescription>
              Select a CSV file containing your client, invoice, and payment data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed rounded-lg p-12 text-center">
              <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <Label htmlFor="csv-upload" className="cursor-pointer">
                <div className="text-lg font-medium mb-2">Click to select a CSV file</div>
                <div className="text-sm text-muted-foreground mb-4">or drag and drop</div>
                <Input
                  id="csv-upload"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileUpload}
                  data-testid="input-csv-upload"
                />
                <Button variant="outline" asChild>
                  <span>Browse Files</span>
                </Button>
              </Label>
            </div>
          </CardContent>
        </Card>
      )}

      {step === "mapping" && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Map CSV Columns to Fields</CardTitle>
              <CardDescription>
                Match each column from your CSV to the appropriate field. Fields are grouped by category.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4">
                {csvColumns.map(column => (
                  <div key={column} className="flex items-center gap-4 p-3 border rounded-lg">
                    <div className="w-1/3">
                      <div className="font-medium">{column}</div>
                      <div className="text-xs text-muted-foreground">
                        Sample: {csvData[0]?.[column]?.slice(0, 50) || "(empty)"}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    <div className="flex-1">
                      <Select
                        value={mappings[column] || "__skip__"}
                        onValueChange={(value) => updateMapping(column, value === "__skip__" ? "" : value)}
                      >
                        <SelectTrigger data-testid={`select-mapping-${column}`}>
                          <SelectValue placeholder="Select target field..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__skip__">-- Do not import --</SelectItem>
                          <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted">Client Fields</div>
                          {CLIENT_FIELDS.filter(f => f.value).map(field => (
                            <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                          ))}
                          <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted">Invoice Fields</div>
                          {INVOICE_FIELDS.filter(f => f.value).map(field => (
                            <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                          ))}
                          <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted">Payment Fields</div>
                          {PAYMENT_FIELDS.filter(f => f.value).map(field => (
                            <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                          ))}
                          <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted">Booking Fields</div>
                          {BOOKING_FIELDS.filter(f => f.value).map(field => (
                            <SelectItem key={field.value} value={field.value}>{field.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {mappings[column] && (
                      <div className="text-xs px-2 py-1 bg-muted rounded">
                        {getFieldCategory(mappings[column])}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={resetImport} data-testid="button-back-upload">
              Back
            </Button>
            <Button onClick={() => setStep("preview")} data-testid="button-continue-preview">
              Continue to Preview
            </Button>
          </div>
        </div>
      )}

      {(step === "preview" || step === "importing") && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Preview Import</CardTitle>
              <CardDescription>
                Review the first 5 records before importing all {csvData.length} records
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {Object.entries(mappings)
                        .filter(([_, target]) => target)
                        .map(([csv, target]) => (
                          <TableHead key={target}>
                            <div className="text-xs text-muted-foreground">{csv}</div>
                            <div>{getFieldLabel(target)}</div>
                          </TableHead>
                        ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.map((row, idx) => (
                      <TableRow key={idx}>
                        {Object.entries(mappings)
                          .filter(([_, target]) => target)
                          .map(([_, target]) => (
                            <TableCell key={target}>{row[target] || "-"}</TableCell>
                          ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Import Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">{csvData.length}</div>
                  <div className="text-sm text-muted-foreground">Total Records</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">
                    {Object.values(mappings).filter(v => v).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Fields Mapped</div>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-2xl font-bold">
                    {mappings["accountName"] || mappings["invoiceNumber"] ? "Ready" : "Missing Required"}
                  </div>
                  <div className="text-sm text-muted-foreground">Status</div>
                </div>
              </div>
              
              <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5" />
                <div>
                  <div className="font-medium">Important Notes</div>
                  <ul className="text-sm text-muted-foreground list-disc list-inside mt-1">
                    <li>All imported records will have automated emails disabled</li>
                    <li>Duplicate invoice numbers will be skipped</li>
                    <li>New regions will be created automatically if needed</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep("mapping")} disabled={step === "importing"} data-testid="button-back-mapping">
              Back to Mapping
            </Button>
            <Button onClick={startImport} disabled={step === "importing"} data-testid="button-start-import">
              {step === "importing" ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import {csvData.length} Records
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {step === "complete" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-6 h-6" />
              Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center mb-6">
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{importResults.imported}</div>
                <div className="text-sm text-muted-foreground">Records Imported</div>
              </div>
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{importResults.skipped}</div>
                <div className="text-sm text-muted-foreground">Records Skipped</div>
              </div>
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{importResults.errors.length}</div>
                <div className="text-sm text-muted-foreground">Errors</div>
              </div>
            </div>

            {importResults.errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="font-medium text-red-600 mb-2">Failed Records ({importResults.errors.length}):</div>
                <div className="max-h-[300px] overflow-y-auto border rounded-md bg-background">
                  {importResults.errors.slice(0, 50).map((error, idx) => (
                    <div key={idx} className="px-3 py-2 border-b last:border-b-0 text-sm">
                      {typeof error === "string" ? error : `Row ${(error as any).row} (${(error as any).accountName}): ${(error as any).error}`}
                    </div>
                  ))}
                  {importResults.errors.length > 50 && (
                    <div className="px-3 py-2 text-muted-foreground italic text-sm">
                      ...and {importResults.errors.length - 50} more errors
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <Button onClick={resetImport} data-testid="button-import-another">
                Import Another File
              </Button>
              <Button variant="outline" onClick={() => window.location.href = "/clients"} data-testid="button-view-clients">
                View Clients
              </Button>
              <Button variant="outline" onClick={() => window.location.href = "/billing"} data-testid="button-view-billing">
                View Billing
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
